import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { AddressComponent } from './address.component';
import { ContactComponent } from './contact.component';

@Component({
  standalone: true,
  selector: 'app-vendor',
  templateUrl: './vendor.component.html',
  styleUrl: './vendor.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatExpansionModule,
    MatSelectModule,
    MatCheckboxModule,
    AddressComponent,
    ContactComponent
  ]
})
export class VendorComponent implements OnInit {
  vendorForm: FormGroup;
  vendorId: number | null = null;
  isEditMode: boolean = false;
  loading: boolean = false;
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.vendorForm = this.fb.group({
      qboVendorId: [null],
      name: ['', Validators.required],
      addresses: this.fb.array([]),  // Address form array
      contacts: this.fb.array([]),   // Contact form array
      accounts: this.fb.array([])    // Account form array
    });
  }

  // Getter for addresses FormArray
  get addresses(): FormArray {
    return this.vendorForm.get('addresses') as FormArray;
  }

  // Getter for contacts FormArray
  get contacts(): FormArray {
    return this.vendorForm.get('contacts') as FormArray;
  }

  // Getter for accounts FormArray
  get accounts(): FormArray {
    return this.vendorForm.get('accounts') as FormArray;
  }

  readonly accountTypes: string[] = ['Shipping'];

  createAccountGroup(account?: any): FormGroup {
    return this.fb.group({
      accountNumber: [account?.accountNumber || account?.AccountNumber || ''],
      name: [account?.name || account?.Name || '', Validators.required],
      provider: [account?.provider || account?.Provider || ''],
      type: [account?.type || account?.Type || ''],
      isActive: [account?.isActive ?? account?.IsActive ?? true],
      notes: [account?.notes || account?.Notes || '']
    });
  }

  getAccountTitle(index: number): string {
    const account = this.accounts.at(index).value;
    if (!account) return `Account ${index + 1}`;
    const name = account.name || '';
    const provider = account.provider || '';
    const type = account.type || '';
    if (name && provider) return `${provider} - ${name}`;
    if (name) return name;
    if (type) return type;
    return `Account ${index + 1}`;
  }

  // Get display title for address
  getAddressTitle(index: number): string {
    const address = this.addresses.at(index).value;
    if (!address) return `Address ${index + 1}`;
    
    const addressType = address.addressType || address.AddressType || '';
    const line1 = address.addressLine1 || address.AddressLine1 || '';
    const city = address.city || address.City || '';
    const state = address.state || address.State || '';
    
    let baseTitle = '';
    if (line1 && city && state) {
      baseTitle = `${line1}, ${city}, ${state}`;
    } else if (line1 && city) {
      baseTitle = `${line1}, ${city}`;
    } else if (line1) {
      baseTitle = line1;
    } else if (city && state) {
      baseTitle = `${city}, ${state}`;
    } else {
      baseTitle = `Address ${index + 1}`;
    }
    
    return addressType ? `${addressType} - ${baseTitle}` : baseTitle;
  }

  // Get display title for contact
  getContactTitle(index: number): string {
    const contact = this.contacts.at(index).value;
    if (!contact) return `Contact ${index + 1}`;
    
    const firstName = contact.firstName || contact.FirstName || '';
    const lastName = contact.lastName || contact.LastName || '';
    const email = contact.email || contact.Email || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (email) {
      return email;
    }
    
    return `Contact ${index + 1}`;
  }

  ngOnInit(): void {
    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.vendorId = +id;
        this.isEditMode = false;
        this.loadVendor(this.vendorId);
      } else {
        this.isEditMode = true;
      }
    });
  }

  enterEditMode(): void {
    this.isEditMode = true;
  }

  loadVendor(id: number): void {
    this.loading = true;
    this.error = null;

    // Clear arrays before (re)loading so cancel/reload always starts fresh
    while (this.addresses.length) this.addresses.removeAt(0);
    while (this.contacts.length) this.contacts.removeAt(0);
    while (this.accounts.length) this.accounts.removeAt(0);

    console.log(`Loading vendor with ID: ${id}`);

    this.apiService.get<any>(`vendor/${id}`).subscribe({
      next: (data) => {
        console.log('Vendor data received:', data);
        
        // Handle both camelCase and PascalCase from API
        const vendor = {
          qboVendorId: data.qBO_Vendor_Id || data.QBO_Vendor_Id || data.qboVendorId,
          name: data.name || data.Name
        };

        // Populate the form with the vendor data
        this.vendorForm.patchValue(vendor);

        // Populate addresses array
        const addressesData = data.addresses || data.Addresses || [];
        addressesData.forEach((addr: any) => {
          this.addresses.push(this.fb.control(addr));
        });

        // Populate contacts array
        const contactsData = data.contacts || data.Contacts || [];
        contactsData.forEach((contact: any) => {
          this.contacts.push(this.fb.control(contact));
        });

        // Populate accounts array
        const accountsData = data.externalAccounts || data.ExternalAccounts || [];
        accountsData.forEach((account: any) => {
          this.accounts.push(this.createAccountGroup(account));
        });

        this.loading = false;
        
        console.log('Form populated with vendor data');
        // Force change detection
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading vendor:', err);
        
        if (err.status === 404) {
          this.error = `Vendor with ID ${id} not found.`;
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log in again.';
        } else {
          this.error = `Failed to load vendor: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  addAddress(): void {
    this.addresses.push(this.fb.control(null));
  }

  removeAddress(index: number): void {
    this.addresses.removeAt(index);
  }

  addContact(): void {
    this.contacts.push(this.fb.control(null));
  }

  removeContact(index: number): void {
    this.contacts.removeAt(index);
  }

  addAccount(): void {
    this.accounts.push(this.createAccountGroup());
  }

  removeAccount(index: number): void {
    this.accounts.removeAt(index);
  }

  onSubmit(): void {
    if (this.vendorForm.valid) {
      const formValue = this.vendorForm.getRawValue();
      const vendorData: any = {
        QBO_Vendor_Id: formValue.qboVendorId,
        Name: formValue.name,
        Addresses: formValue.addresses,
        Contacts: formValue.contacts,
        ExternalAccounts: formValue.accounts
      };

      this.loading = true;
      this.error = null;

      if (this.vendorId) {
        vendorData.Id = this.vendorId;
        this.apiService.put<any>(`vendor/${this.vendorId}`, vendorData).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/vendors']);
          },
          error: (err) => {
            console.error('Error updating vendor:', err);
            this.error = `Failed to update vendor: ${err.message || 'Unknown error'}`;
            this.loading = false;
          }
        });
      } else {
        this.apiService.post<any>('vendor', vendorData).subscribe({
          next: () => {
            this.loading = false;
            this.router.navigate(['/vendors']);
          },
          error: (err) => {
            console.error('Error creating vendor:', err);
            this.error = `Failed to create vendor: ${err.message || 'Unknown error'}`;
            this.loading = false;
          }
        });
      }
    }
  }

  onCancel(): void {
    if (this.vendorId) {
      this.isEditMode = false;
      this.loadVendor(this.vendorId);
    } else {
      this.router.navigate(['/vendors']);
    }
  }
}
